import { Badge, Button, Card, Group, Modal, Stack, Table, Text, Textarea, TextInput } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconSearch, IconUpload } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { useMe } from '../../lib/me';
import type { DirUser } from '../../lib/types';
import { PageHeader } from '../../ui/PageHeader';
import { QueryState } from '../../ui/QueryState';

const SAMPLE = 'employee_id;full_name;email;department;timezone;active\nE-3001;Николай Сидоров;n.sidorov@chiron.demo;Support;Europe/Moscow;1\nE-2010;Наталья Белова;n.belova@chiron.demo;Operations;Europe/Moscow;0';

export function UsersPage() {
  const me = useMe();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [opened, { open, close }] = useDisclosure(false);
  const [csv, setCsv] = useState(SAMPLE);
  const users = useQuery({ queryKey: ['dir-users'], queryFn: () => api.get<DirUser[]>('/admin/users') });
  const imp = useMutation({
    mutationFn: () => api.post<{ created: number; updated: number; deactivated: number; errors: string[] }>('/admin/users/import', { csv }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['dir-users'] });
      await qc.invalidateQueries({ queryKey: ['departments'] });
      close();
      notifications.show({ color: r.errors.length ? 'orange' : 'teal', title: `Создано ${r.created}, обновлено ${r.updated}, отключено ${r.deactivated}`, message: r.errors.join('; ') || 'Без ошибок' });
    },
    onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
  });
  return (
    <>
      <PageHeader
        title="Пользователи и роли"
        subtitle="Справочник сотрудников. Роли команды обучения приходят из групп IdP и здесь только отображаются. Отключённый сотрудник не входит, его история сохраняется."
        actions={me.roles.includes('ASSIGNMENTS') && <Button leftSection={<IconUpload size={16} />} variant="default" onClick={open}>Импорт CSV</Button>}
      />
      <TextInput placeholder="Поиск по ФИО, email, табельному, подразделению" leftSection={<IconSearch size={14} />} value={search} onChange={(e) => setSearch(e.currentTarget.value)} mb="md" maw={380} />
      <QueryState loading={users.isLoading} error={users.error}>
        {() => {
          const s = search.toLowerCase();
          const list = users.data!.filter((u) => !s || `${u.fullName} ${u.email} ${u.employeeId} ${u.department}`.toLowerCase().includes(s));
          return (
            <Card withBorder radius="lg" p={0}>
              <Table.ScrollContainer minWidth={980}>
                <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
                  <Table.Thead><Table.Tr><Table.Th>Сотрудник</Table.Th><Table.Th>Табельный</Table.Th><Table.Th>Подразделение</Table.Th><Table.Th>Часовой пояс</Table.Th><Table.Th>Роли (IdP)</Table.Th><Table.Th>Учётка</Table.Th><Table.Th>Последний вход</Table.Th><Table.Th>Статус</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>
                    {list.map((u) => (
                      <Table.Tr key={u.id} opacity={u.active ? 1 : 0.55}>
                        <Table.Td><Text size="sm" fw={500}>{u.fullName}</Text><Text size="xs" c="dimmed">{u.email}</Text></Table.Td>
                        <Table.Td><Text size="sm">{u.employeeId ?? '-'}</Text></Table.Td>
                        <Table.Td><Text size="sm">{u.department ?? '-'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{u.timezone}</Text></Table.Td>
                        <Table.Td><Group gap={4}>{u.roles.length ? u.roles.map((r) => <Badge key={r} size="xs" variant="light">{r}</Badge>) : <Text size="xs" c="dimmed">сотрудник</Text>}</Group></Table.Td>
                        <Table.Td>{u.linked ? <Badge size="sm" variant="light" color="teal">привязана</Badge> : <Badge size="sm" variant="outline" color="gray">не входил</Badge>}</Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{fmtDateTime(u.lastLoginAt)}</Text></Table.Td>
                        <Table.Td>{u.anonymized ? <Badge size="sm" color="gray">обезличен</Badge> : u.active ? <Badge size="sm" color="teal" variant="light">активен</Badge> : <Badge size="sm" color="dark" variant="light">отключён</Badge>}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Card>
          );
        }}
      </QueryState>
      <Modal opened={opened} onClose={close} title="Импорт справочника из CSV" size="lg" centered>
        <Stack>
          <Text size="sm" c="dimmed">Колонки: employee_id; full_name; email; department; timezone; active (1/0). Сотрудники сопоставляются по employee_id. active=0 - отключение (offboarding): вход закрывается, история сохраняется.</Text>
          <Textarea autosize minRows={8} styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }} value={csv} onChange={(e) => setCsv(e.currentTarget.value)} />
          <Group justify="space-between">
            <input type="file" accept=".csv,text/csv" onChange={(e) => e.target.files?.[0]?.text().then(setCsv)} />
            <Group><Button variant="default" onClick={close}>Отмена</Button><Button loading={imp.isPending} onClick={() => imp.mutate()}>Импортировать</Button></Group>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
